"use client";

import { useState } from "react";
import { toast } from "sonner";

import {
  useBrokerCredentials,
  type BrokerCredentialRow,
} from "@/hooks/useBrokerCredentials";
import type { MarketDataCredentialStatus } from "@/lib/api";

/**
 * SFWL-006 Phase 2: BYO broker credential management ("Execution Venues").
 *
 * One card per supported broker (CQG today). Org admins supply their own broker
 * login; "Test connection" runs the backend validate ping. Credentials are
 * encrypted at rest and only ever read back masked.
 */
export function FinancialsSettings() {
  const { rows, isLoading, error } = useBrokerCredentials();

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">Execution Venues</h2>
        <p className="mt-1 text-sm text-slate-500">
          Connect your brokerage accounts for multi-venue execution. Keys are encrypted
          and never displayed in full.
        </p>
      </div>

      {isLoading && <p className="text-sm text-slate-400">Loading venues…</p>}

      {error && (
        <p className="text-sm text-red-600">
          Couldn&apos;t load broker credentials: {error.message}
        </p>
      )}

      <div className="space-y-4">
        {rows.map((row) => (
          <BrokerCard key={row.broker.id} row={row} />
        ))}

        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="mb-3">
            <h3 className="text-sm font-semibold text-slate-900">Crypto Wallet</h3>
            <p className="mt-0.5 text-xs text-slate-500">
              Connect your wallet for on-chain execution via Hyperliquid.
            </p>
          </div>
          <p className="text-sm text-slate-400 italic">Wallet connection coming soon.</p>
        </div>
      </div>
    </div>
  );
}

function BrokerCard({ row }: { row: BrokerCredentialRow }) {
  const { broker, credential } = row;
  const {
    saveCredential,
    validateCredential,
    deleteCredential,
    isSaving,
    isValidating,
    isDeleting,
  } = useBrokerCredentials();

  const [values, setValues] = useState<Record<string, string>>({});
  // Derived, not initialized-from-props: `credential` is null while the list
  // query is in flight, so a mount-time `useState(!credential)` would leave a
  // saved credential stuck on the blank replacement form once data arrives.
  // `editingOverride` only tracks an explicit "Replace" click.
  const [editingOverride, setEditingOverride] = useState(false);
  const editing = editingOverride || !credential;

  const handleSave = async () => {
    const payload: Record<string, string> = {};
    for (const f of broker.fields) {
      const v = values[f.key]?.trim();
      if (!v) {
        toast.error(`${f.label} is required.`);
        return;
      }
      payload[f.key] = v;
    }
    try {
      await saveCredential({ broker: broker.id, payload });
      toast.success(`${broker.displayName} credentials saved.`);
      setValues({});
      setEditingOverride(false);
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const handleValidate = async () => {
    if (!credential) return;
    try {
      const res = await validateCredential(credential.id);
      if (res.status === "valid") {
        // Honest copy: today's check verifies the credential format server-side;
        // a live CQG logon ping lands with live order routing (post-demo).
        toast.success(
          `${broker.displayName} credential format verified. Full connection test arrives with live order routing.`
        );
      } else {
        toast.error(res.error ?? "Credential check failed.");
      }
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const handleDelete = async () => {
    if (!credential) return;
    try {
      await deleteCredential(credential.id);
      toast.success(`${broker.displayName} credentials removed.`);
      setEditingOverride(false); // credential is now null → form shows via derivation
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">{broker.displayName}</h3>
          <p className="mt-0.5 text-xs text-slate-500">
            Futures execution (ES, NQ, GC, SI).
          </p>
        </div>
        {credential && <StatusBadge status={credential.status} />}
      </div>

      {credential && !editing && (
        <div className="space-y-3">
          <dl className="space-y-1">
            {Object.entries(credential.maskedPayload).map(([k, v]) => (
              <div key={k} className="flex justify-between text-xs">
                <dt className="text-slate-500">{k}</dt>
                <dd className="font-mono text-slate-700">{v}</dd>
              </div>
            ))}
          </dl>
          {credential.lastValidatedAt && (
            <p className="text-xs text-slate-400">
              Last tested {new Date(credential.lastValidatedAt).toLocaleString()}
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleValidate}
              disabled={isValidating}
              className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 transition hover:bg-slate-50 disabled:opacity-50"
            >
              {isValidating ? "Testing…" : "Test connection"}
            </button>
            <button
              type="button"
              onClick={() => setEditingOverride(true)}
              className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 transition hover:bg-slate-50"
            >
              Replace
            </button>
            <button
              type="button"
              onClick={handleDelete}
              disabled={isDeleting}
              className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 transition hover:bg-red-50 disabled:opacity-50"
            >
              {isDeleting ? "Removing…" : "Remove"}
            </button>
          </div>
        </div>
      )}

      {editing && (
        <div className="space-y-3">
          {broker.fields.map((f) => (
            <div key={f.key}>
              <label className="mb-1 block text-xs font-medium text-slate-600">
                {f.label}
              </label>
              <input
                type={f.secret ? "password" : "text"}
                value={values[f.key] ?? ""}
                onChange={(e) =>
                  setValues((prev) => ({ ...prev, [f.key]: e.target.value }))
                }
                placeholder={`Enter ${f.label.toLowerCase()}…`}
                className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-indigo-300 focus:outline-none focus:ring-1 focus:ring-indigo-300"
              />
            </div>
          ))}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:opacity-50"
            >
              {isSaving ? "Saving…" : "Save Keys"}
            </button>
            {credential && (
              <button
                type="button"
                onClick={() => {
                  setEditingOverride(false);
                  setValues({});
                }}
                className="rounded-lg px-3 py-2 text-sm font-medium text-slate-500 transition hover:text-slate-700"
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// "Format verified" (not "Connected") — the validate check confirms the
// credential's shape server-side; a live CQG logon test is a post-demo follow-up.
const STATUS_LABEL: Record<MarketDataCredentialStatus, string> = {
  valid: "Format verified",
  invalid: "Invalid",
  unverified: "Not tested",
};

function StatusBadge({ status }: { status: MarketDataCredentialStatus }) {
  const tone =
    status === "valid"
      ? "bg-emerald-50 text-emerald-700"
      : status === "invalid"
        ? "bg-red-50 text-red-700"
        : "bg-amber-50 text-amber-700";
  return (
    <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ${tone}`}>
      {STATUS_LABEL[status]}
    </span>
  );
}
